import { animate } from 'motion';
import debounce from 'lodash/debounce';

import jump from '../../utils/jump';
import Logger from '../../utils/logger/logger';
import request from '../../utils/request/request';

import analytics, { events } from '../../../shared/analytics';
import config from '../../../shared/config/config';

import breakpoints from '../../config/breakpoints';

import BasePage from '../../components/base-page/base-page';
import CategoryTabs from '../../components/category-tabs/category-tabs';
import Carousel from '../../components/carousel/carousel';
import AllApps from '../../components/all-apps/all-apps';
import GoUpButton from '../../components/go-up-button/go-up-button';

import './applications.scss';


class ApplicationsPage extends BasePage {
  allApps?: typeof AllApps;

  ui = {
    searchInput: '.js-apps-search-input',
    appsContentColumn: '.js-apps-content-column',
    categoriesSelector: '.js-categories-selector',
    newlyAddedAppsCarousel: '.js-newly-added',
    newlyAddedSection: '.js-newly-added-section',
    useCasesCarousel: '.js-use-cases',
    useCasesSection: '.js-use-cases-section',
    allAppsTitle: '.js-all-apps-title',
    clearSearchButton: '.js-clear-search-btn',
    currentCategoryTab: '[aria-current="page"]',
  };

  events = {
    'input @ui.searchInput': 'handleSearchQueryInput',
    'click @ui.searchInput': 'scrollToSearchInput',
    'click @ui.clearSearchButton': 'handleSearchClear',
  };

  isSearchResultVisible = false;

  stickyNavigationHeightOffset = -100;

  activeCategoryTabClass = 'applications-categories__item_active';
  hiddenClearButtonClass = 'applications-header__search-close-btn_hidden';

  onInit() {
    super.onInit();

    new CategoryTabs().init();

    if (this.ui.useCasesCarousel) {
      new Carousel({
        rootElement: this.ui.useCasesCarousel,
        interval: 5000,
        linksSelector: '.js-use-case-wrap-link',
      }).init();
    }

    if (this.ui.newlyAddedAppsCarousel) {
      new Carousel({
        rootElement: this.ui.newlyAddedAppsCarousel,
        slidesPerPage: {
          [breakpoints.sm]: 2,
        },
        linksSelector: '.js-app-info-link',
      }).init();
    }

    this.allApps = new AllApps();
    this.allApps.init();

    new GoUpButton(this.ui.allAppsTitle).init();
  }

  handleSearchClear = () => {
    (this.ui.searchInput as unknown as HTMLInputElement).value = '';
    this.animateAppsContentUpdate(() => this.resetAppsSearch());
  };

  handleSearchQueryInput = debounce((e) => {
    const { value } = e.target;
    (this.ui.clearSearchButton as unknown as HTMLInputElement).classList.remove(this.hiddenClearButtonClass);

    this.searchForApps(value);
  }, 500);

  searchForApps = (query) => {
    if (!query) {
      if (this.isSearchResultVisible) {
        this.animateAppsContentUpdate(() => this.resetAppsSearch());
      }

      return Promise.resolve();
    }

    return request.get(`${config.get('applicationsApi')}?q=${encodeURIComponent(query)}`)
      .then((appIds) => {
        if (this.isSearchResultVisible) {
          this.showAppsSearchResults(appIds, query);
        } else {
          this.handleAppsSearchSuccess(appIds, query);
        }
        analytics.trackEvent({
          ...events.SEARCH_USED,
          eventLabel: query,
        });
      })
      .catch(this.handleAppsSearchError);
  };

  handleAppsSearchSuccess = (appIds, query) => {
    this.animateAppsContentUpdate(() => this.showAppsSearchResults(appIds, query));
  };

  handleAppsSearchError = (err) => {
    Logger.catchError('Apps search failed', err);
  };

  showAppsSearchResults = (appIds, query) => {
    if (this.ui.newlyAddedSection) {
      (this.ui.newlyAddedSection as unknown as HTMLElement).classList.add('applications__newly-added_hidden');
    }

    if (this.ui.useCasesCarousel) {
      (this.ui.useCasesSection as unknown as HTMLElement).classList.add('applications__use-cases_hidden');
    }

    (this.ui.categoriesSelector as unknown as HTMLElement).classList.add('d-none');

    const currentCategory = this.ui.currentCategoryTab as unknown as HTMLElement;
    currentCategory.classList.remove(this.activeCategoryTabClass);
    currentCategory.setAttribute('aria-current', 'false');

    this.allApps.showSearchResult(appIds, query);

    this.isSearchResultVisible = true;
  };

  resetAppsSearch = () => {
    if (this.ui.newlyAddedSection) {
      (this.ui.newlyAddedSection as unknown as HTMLElement).classList.remove('applications__newly-added_hidden');
    }

    if (this.ui.useCasesCarousel) {
      (this.ui.useCasesSection as unknown as HTMLElement).classList.remove('applications__use-cases_hidden');
    }

    (this.ui.categoriesSelector as unknown as HTMLElement).classList.remove('d-none');
    (this.ui.clearSearchButton as unknown as HTMLInputElement).classList.add(this.hiddenClearButtonClass);

    const currentCategory = this.ui.currentCategoryTab as unknown as HTMLElement;
    currentCategory.classList.add(this.activeCategoryTabClass);
    currentCategory.setAttribute('aria-current', 'true');

    this.allApps.resetSearchResult();

    this.isSearchResultVisible = false;
  };

  animateAppsContentUpdate = (updater) => {
    animate(this.ui.appsContentColumn,
      { opacity: 0 },
      { duration: 0.2 }
    ).finished.then(() => {
      updater();

      animate(this.ui.appsContentColumn,
        { opacity: 1 },
        { duration: 0.2 }
      );
    });
  };

  scrollToSearchInput = () => {
    jump(this.ui.searchInput, {
      offset: this.stickyNavigationHeightOffset,
    });
  };
}

export default ApplicationsPage;

(ApplicationsPage as BasePage).initOnDomLoaded();
